import i18n from 'i18next';
import type { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { enResource, i18nLanguages, loadLanguageResource } from './locales';
import { navByLang, toolsByLang } from './locales/newLandingNav';
import { PAGE_LANGUAGE_CODES } from '@shared/pageLanguages';

// Lazy backend: English is bundled up front (see `resources` below); every
// other language is fetched as its own chunk the first time it is requested.
// This keeps ~1MB of translation data out of the initial download.
const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    loadLanguageResource(language)
      .then((resource) => callback(null, resource ?? false))
      .catch((error) => callback(error as Error, false));
  },
};

// A page language (shared/pageLanguages.ts: Thai, Traditional Chinese) is
// switched on by its URL and by nothing else. The stock `navigator` detector
// would start a Thai browser in Thai on every English URL (and a browser set
// to Twi, whose ISO code is "tw", in Traditional Chinese) until the router
// corrected it, downloading that language's strings for nothing. This one
// reports the browser's languages as the stock detector does, minus the page
// languages; `excludeCacheFor` below keeps them out of localStorage for the
// same reason. For the 14 site languages nothing changes.
const isPageLanguage = (value: unknown) =>
  PAGE_LANGUAGE_CODES.includes(
    String(value).toLowerCase().split(/[-_]/)[0] ?? '',
  );
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'siteNavigator',
  lookup() {
    if (typeof navigator === 'undefined') return undefined;
    const found: string[] = [];
    const { languages, language } = navigator;
    const { userLanguage } = navigator as Navigator & { userLanguage?: string };
    if (languages) {
      for (let i = 0; i < languages.length; i++) found.push(languages[i]!);
    }
    if (userLanguage) found.push(userLanguage);
    if (language) found.push(language);
    const kept = found.filter((code) => !isPageLanguage(code));
    return kept.length > 0 ? kept : undefined;
  },
});

i18n
  // Load non-bundled languages on demand
  .use(lazyBackend)
  // Detect user language
  .use(languageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Init i18next
  .init({
    // Only English is bundled synchronously; other languages come from
    // lazyBackend. partialBundledLanguages tells i18next to use the backend
    // even though some resources are provided inline.
    resources: { en: { translation: enResource } },
    partialBundledLanguages: true,
    fallbackLng: 'en',
    // The 14 site languages and the page languages (see above).
    supportedLngs: [...i18nLanguages],
    debug: false,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      // Order of detection ('siteNavigator': the browser's languages, see
      // above)
      order: ['path', 'localStorage', 'siteNavigator'],

      // Keys to lookup language from
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,

      // Cache user language (never a page language)
      caches: ['localStorage'],
      excludeCacheFor: ['cimode', ...PAGE_LANGUAGE_CODES],
    },

    // Allow "en-US" to fall back to "en"
    load: 'languageOnly',

    // Non-English translations load asynchronously. Render immediately with the
    // English fallback instead of suspending the whole tree while a language
    // chunk downloads.
    react: {
      useSuspense: false,
    },
  });

// The site-wide GlobalNav (rendered by Layout on every page, including first
// paint) needs its strings synchronously in EVERY language, so the small
// nav/tools bundle is registered up front for all languages here. The heavy
// page namespaces (newLanding, lyricsVideo) are registered lazily by the
// chunks that use them — see locales/registerNewLandingI18n.ts.
for (const code of i18nLanguages) {
  i18n.addResourceBundle(
    code,
    'translation',
    {
      globalNav: {
        nav: navByLang[code] ?? navByLang.en,
        tools: toolsByLang[code] ?? toolsByLang.en,
      },
    },
    true,
    true,
  );
}

export default i18n;
